import { FormEvent, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import type { ModelInfo, OrgSlackConfigSummary } from 'chat-shared'
import { api, ApiError } from '../api/client.js'
import { AdminCard } from './AdminUi.js'

/** Wert der Dropdown-Option „Server-Default" (kein Org-Override). */
const MODEL_DEFAULT = ''

type OrgSlackSectionProps = {
  orgId: string
}

// Slack-Anbindung der Organisation: Bot-Token + Signing-Secret der org-
// eigenen Slack-App. Beim Speichern eines neuen Tokens validiert der Server
// es per auth.test und übernimmt die Workspace-Identität; beim Aktivieren
// provisioniert er Service-Konto, Ordner „Slack-Bot" und den Bot-API-
// Schlüssel automatisch (siehe apps/chat-api/src/slack/provisioning.ts).
export const OrgSlackSection = ({ orgId }: OrgSlackSectionProps) => {
  const [config, setConfig] = useState<OrgSlackConfigSummary | undefined>(undefined)
  const [botToken, setBotToken] = useState('')
  const [signingSecret, setSigningSecret] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [model, setModel] = useState<string>(MODEL_DEFAULT)
  const [models, setModels] = useState<Array<ModelInfo>>([])
  const [hasStoredToken, setHasStoredToken] = useState(false)
  const [hasStoredSecret, setHasStoredSecret] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [saved, setSaved] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const applyConfig = (summary: OrgSlackConfigSummary | undefined) => {
    setConfig(summary)
    setEnabled(summary?.enabled ?? false)
    setModel(summary?.model ?? MODEL_DEFAULT)
    setHasStoredToken(summary?.hasBotToken ?? false)
    setHasStoredSecret(summary?.hasSigningSecret ?? false)
    setBotToken('')
    setSigningSecret('')
  }

  useEffect(() => {
    api.getOrgSlackConfig(orgId).then(applyConfig).catch(() => applyConfig(undefined))
    api.models().then(setModels).catch(() => setModels([]))
    setError(undefined)
    setSaved(false)
  }, [orgId])

  const handleSave = async (event: FormEvent) => {
    event.preventDefault()
    setError(undefined)
    setSaved(false)
    try {
      const summary = await api.saveOrgSlackConfig(orgId, {
        enabled,
        // Leere Felder ⇒ vorhandene Secrets behalten (write-only).
        botToken: botToken.length > 0 ? botToken : undefined,
        signingSecret: signingSecret.length > 0 ? signingSecret : undefined,
        // „Server-Default" ⇒ kein Override speichern.
        model: model.length > 0 ? model : undefined,
      })
      applyConfig(summary)
      setSaved(true)
    } catch (err) {
      if (err instanceof ApiError && err.message === 'credentials_required') {
        setError('Zum Aktivieren müssen Bot-Token und Signing-Secret hinterlegt sein.')
      } else if (err instanceof ApiError && err.message === 'invalid_bot_token') {
        setError(
          err.detail ?? 'Slack hat das Bot-Token abgelehnt (auth.test). Bitte Token prüfen.',
        )
      } else if (err instanceof ApiError && err.message === 'slack_unreachable') {
        setError(
          `Slack ist von der chat-api aus nicht erreichbar${err.detail ? ` (${err.detail})` : ''} — Details im chat-api-Log.`,
        )
      } else {
        setError('Speichern fehlgeschlagen')
      }
    }
  }

  const handleDelete = async () => {
    setError(undefined)
    try {
      await api.deleteOrgSlackConfig(orgId)
      applyConfig(undefined)
      setConfirmDelete(false)
    } catch {
      setError('Löschen fehlgeschlagen')
      setConfirmDelete(false)
    }
  }

  return (
    <AdminCard
      title="Slack"
      description="Verbindet den Slack-Bot („@HappyOps“) dieser Organisation. Benötigt eine eigene Slack-App (per App-Manifest in wenigen Minuten angelegt, siehe apps/slack-bot/README.md) — Bot-Token (xoxb-…) und Signing-Secret der App. Chats des Bots erscheinen im automatisch angelegten Ordner „Slack-Bot“."
      padded
    >
      {error && (
        <Alert severity="error" onClose={() => setError(undefined)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" onClose={() => setSaved(false)} sx={{ mb: 2 }}>
          Gespeichert.{' '}
          {config?.enabled && 'Die Slack-Anbindung ist aktiv; der Ordner „Slack-Bot“ wurde angelegt.'}
        </Alert>
      )}
      <Box component="form" onSubmit={handleSave} display="flex" flexDirection="column" gap={2} maxWidth={480}>
        {config?.teamId && (
          <Typography variant="body2" color="text.secondary">
            Verbundener Workspace: {config.teamName ? `${config.teamName} ` : ''}({config.teamId})
          </Typography>
        )}
        <TextField
          size="small"
          type="password"
          label="Bot-Token (xoxb-…)"
          value={botToken}
          onChange={(e) => setBotToken(e.target.value)}
          placeholder={hasStoredToken ? '••••••••  (gespeichert — leer lassen zum Behalten)' : ''}
          required={!hasStoredToken}
          helperText="Slack-App → „OAuth & Permissions“ → „Bot User OAuth Token“ (erscheint nach „Install to Workspace“). Nicht das App-Level-Token (xapp-…)."
        />
        <TextField
          size="small"
          type="password"
          label="Signing-Secret"
          value={signingSecret}
          onChange={(e) => setSigningSecret(e.target.value)}
          placeholder={hasStoredSecret ? '••••••••  (gespeichert — leer lassen zum Behalten)' : ''}
          required={!hasStoredSecret}
          helperText="Slack-App → „Basic Information“ → „App Credentials“ → „Signing Secret“."
        />
        <TextField
          size="small"
          select
          label="KI-Modell für Bot-Chats"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          helperText="Gilt für neue Bot-Chats; pro Slack-Thread per „@HappyOps set model“ überschreibbar."
        >
          <MenuItem value={MODEL_DEFAULT}>Server-Default</MenuItem>
          {models.map((entry) => (
            <MenuItem key={entry.id} value={entry.id}>
              {entry.label}
            </MenuItem>
          ))}
          {model !== MODEL_DEFAULT && !models.some((entry) => entry.id === model) && (
            <MenuItem value={model}>{model} (deaktiviert)</MenuItem>
          )}
        </TextField>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label="Slack-Anbindung aktiv"
        />
        <Box display="flex" gap={1}>
          <Button type="submit" variant="contained">
            Speichern
          </Button>
          {config && (
            <Button color="error" onClick={() => setConfirmDelete(true)}>
              Anbindung löschen
            </Button>
          )}
        </Box>
      </Box>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Slack-Anbindung löschen?</DialogTitle>
        <DialogContent>
          Die Bot-Zugangsdaten werden entfernt und der API-Schlüssel des Bots widerrufen. Der Ordner
          „Slack-Bot“ und die bisherigen Chats bleiben erhalten.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleDelete()}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </AdminCard>
  )
}
